import { commitMutation, Environment, graphql } from 'react-relay';
import { MutationConfig } from 'relay-runtime';

import type {
	CreateCustomFieldMutation as CreateCustomFieldMutationType,
	TeamCustomFieldCardinality,
	TeamCustomFieldType,
} from './__generated__/CreateCustomFieldMutation.graphql';

export interface CreateCustomFieldInput {
	name: string;
	description?: string;
	type: TeamCustomFieldType;
	cardinality: TeamCustomFieldCardinality;
	options?: string[];
}

export const createCustomFieldMutation = (
	environment: Environment,
	input: {
		customFieldData: CreateCustomFieldInput;
		scopeId: string;
	},
	events: Pick<MutationConfig<CreateCustomFieldMutationType>, 'onCompleted' | 'onError'>,
) => {
	return commitMutation<CreateCustomFieldMutationType>(environment, {
		mutation: graphql`
			mutation CreateCustomFieldMutation($customFieldData: TeamCreateCustomFieldPayload!, $scopeId: ID!) {
				team {
					createCustomField(customFieldData: $customFieldData, scopeId: $scopeId) @optIn(to: "Team-custom-fields") {
						id
						name
						description
						type
					}
				}
			}
		`,
		variables: {
			customFieldData: input.customFieldData,
			scopeId: input.scopeId,
		},
		updater: (store, data) => {
			const newField = data?.team?.createCustomField;
			if (!newField) {
				return;
			}

			const teamRecord = store.getRoot().getLinkedRecord('team');
			if (!teamRecord) {
				return;
			}

			let newFieldRecord = store.get(newField.id);
			if (!newFieldRecord || newFieldRecord.getType() !== 'TeamCustomFieldInfo') {
				newFieldRecord = store.create(newField.id, 'TeamCustomFieldInfo');
			}
			newFieldRecord.setValue(newField.id, 'id');
			newFieldRecord.setValue(newField.name, 'name');
			newFieldRecord.setValue(newField.description ?? null, 'description');
			newFieldRecord.setValue(newField.type, 'type');

			const existing = teamRecord.getLinkedRecords('customFieldsWithinScope', { scopeId: input.scopeId }) ?? [];
			teamRecord.setLinkedRecords([...existing, newFieldRecord], 'customFieldsWithinScope', {
				scopeId: input.scopeId,
			});
		},
		onCompleted: events.onCompleted,
		onError: events.onError,
	});
};
